import { extent, max } from 'd3-array';
import { scaleLinear, scaleTime } from 'd3-scale';
import { area, curveMonotoneX, line } from 'd3-shape';
import { timeParse } from 'd3-time-format';

import Color from 'src/constants/color';
import Rate from 'src/constants/rate';

import { LineChartData, LineChartPoint, LineChartRawData, XAxis, YAxis } from 'src/components/LineChart/types';

const d3 = { scaleLinear, scaleTime, extent, max, area, line, timeParse };

interface BorderValues {
    date: {
        min: Date;
        max: Date;
    };
    value: {
        min: number;
        max: number;
    };
}

export const DATE_FORMAT = '%Y-%m-%d';

export const parseDate = d3.timeParse(DATE_FORMAT);

export const sortByDate = (data: LineChartRawData): LineChartRawData => {
    return data.sort((a, b) => {
        if (a.date > b.date) {
            return 1;
        } else if (a.date < b.date) {
            return -1;
        }
        return 0;
    });
};

/** Возвращает минимальное и максимальное значения для осей OX (date) и OY (number) */
export const getBorderAxesValues = (data: LineChartRawData): BorderValues => {
    const sortedData = sortByDate(data);
    const minDate = parseDate(sortedData[0].date) as Date;
    const maxDate = parseDate(sortedData[sortedData.length - 1].date) as Date;

    const values = data.map((point) => point.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    return {
        date: {
            min: minDate,
            max: maxDate,
        },
        value: {
            min: minValue,
            max: maxValue,
        },
    };
};

/** Возвращает минимальное и максимальное значения для осей OX (date) и OY (number) для нескольких графиков */
export const getBorderAxesValuesForMultipleLines = (data: LineChartRawData[]): BorderValues => {
    return data
        .map((lineData) => getBorderAxesValues(lineData))
        .reduce((previouse, current) => {
            return {
                date: {
                    min: current.date.min < previouse.date.min ? current.date.min : previouse.date.min,
                    max: current.date.max > previouse.date.max ? current.date.max : previouse.date.max,
                },
                value: {
                    min: Math.min(previouse.value.min, current.value.min),
                    max: Math.max(previouse.value.max, current.value.max),
                },
            };
        });
};

export const getPreparedData = (lineData: LineChartRawData): LineChartData => {
    return lineData.map((point) => {
        const additionalData = point.additional ? { additional: point.additional } : {};
        return {
            rawDate: point.date,
            date: parseDate(point.date) as Date,
            value: point.value,
            ...additionalData,
        };
    });
};

export const getXAxis = (min: Date, max: Date, width: number): XAxis =>
    d3.scaleTime().domain([min, max]).range([0, width]);

export const getYAxis = (min: number, max: number, height: number): YAxis =>
    d3.scaleLinear().domain([min, max]).range([height, 0]);

export const getLinePath = (
    preparedData: LineChartData,
    xAxis: XAxis,
    yAxis: YAxis,
    isCurveChart = false
): string | null => {
    const lineChart = d3
        .line<LineChartPoint>()
        .defined((point) => point.value !== null)
        .x((point) => xAxis(point.date))
        .y((point) => yAxis(point.value));

    if (isCurveChart) {
        lineChart.curve(curveMonotoneX);
    }

    const filteredLineData = preparedData.filter(lineChart.defined());
    return lineChart(filteredLineData);
};

export const getAreaPath = (
    preparedData: LineChartData,
    xAxis: XAxis,
    yAxis: YAxis,
    yMin: number,
    isCurveChart = false
): string | null => {
    const areaChart = d3
        .area<LineChartPoint>()
        .defined((point) => point.value !== null)
        .x((point) => xAxis(point.date))
        .y0(yAxis(yMin))
        .y1((point) => yAxis(point.value));

    if (isCurveChart) {
        areaChart.curve(curveMonotoneX);
    }

    const filteredLineData = preparedData.filter(areaChart.defined());
    return areaChart(filteredLineData);
};

interface ChartColorsParams {
    rate?: Rate;
    startValue?: number;
    endValue?: number;
    positiveRate?: Rate;
    colors?: Color[];
}
/**
 * Рассчитывает цвет для графика в зависимости от его значений.
 * Например, делать график зеленым, если график растет, красным – если падает, синим – если не изменяется
 * Если positiveRate < 0, то наоборот, график будет зеленым, если он падает.
 * Если positiveRate = 0, то, график всегда красится в один и тот же цвет.
 */
export const calculateChartColor = ({
    rate,
    startValue,
    endValue,
    positiveRate = Rate.Increase,
    colors = [Color.NormalBlue, Color.Positive, Color.Negative],
}: ChartColorsParams): Color => {
    const [staticColor, increaseColor, decreaseColor] = colors;
    let calculatedRate = 0;
    if (rate !== undefined) {
        calculatedRate = rate;
    } else if (endValue !== undefined && startValue !== undefined) {
        calculatedRate = endValue - startValue;
    }
    calculatedRate *= positiveRate;

    if (calculatedRate > 0) {
        return increaseColor;
    } else if (calculatedRate < 0) {
        return decreaseColor;
    }
    return staticColor;
};
